import { useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

type Crumb = { label: string; to?: string };

interface PanelVariant {
  prefix: string; // "/producer" | "/admin"
  rootLabelKey: string;
  rootPath: string;
  eventsSeg: string; // "events" | "eventos"
  panelSlug: string; // "panel" | "painel"
  editVerb: string; // "edit" | "editar"
  // Valores são chaves de tradução (não texto), resolvidas via t() no consumo.
  sectionLabelKeys: Record<string, string>;
  panelTabLabelKeys: Record<string, string>;
}

// Chave de fallback compartilhada ("Dashboard") usada tanto na raiz do painel
// quanto no rótulo de aba vazia.
const DASHBOARD_KEY = "breadcrumbs.panelTabs.dashboard";

// Seções de edição inline (?editorStep=...) — comum aos dois painéis.
// Valores são chaves de tradução, não texto.
const EDIT_STEP_KEYS: Record<string, string> = {
  info: "breadcrumbs.editSteps.info",
  venue: "breadcrumbs.editSteps.venue",
  form: "breadcrumbs.editSteps.form",
  products: "breadcrumbs.editSteps.products",
  settings: "breadcrumbs.editSteps.settings",
};

// Subníveis da Programação (PT + EN) — chaves de tradução.
const SCHEDULE_SUB_KEYS: Record<string, string> = {
  atividades: "breadcrumbs.scheduleSections.activities",
  activities: "breadcrumbs.scheduleSections.activities",
  nova: "breadcrumbs.scheduleSections.new",
  new: "breadcrumbs.scheduleSections.new",
  editar: "breadcrumbs.scheduleSections.edit",
  edit: "breadcrumbs.scheduleSections.edit",
  operacao: "breadcrumbs.scheduleSections.operation",
  operation: "breadcrumbs.scheduleSections.operation",
  palestrantes: "breadcrumbs.scheduleSections.speakers",
  speakers: "breadcrumbs.scheduleSections.speakers",
  espacos: "breadcrumbs.scheduleSections.spaces",
  spaces: "breadcrumbs.scheduleSections.spaces",
  materiais: "breadcrumbs.scheduleSections.materials",
  materials: "breadcrumbs.scheduleSections.materials",
  perguntas: "breadcrumbs.scheduleSections.questions",
  questions: "breadcrumbs.scheduleSections.questions",
  publicar: "breadcrumbs.scheduleSections.publish",
  publish: "breadcrumbs.scheduleSections.publish",
  config: "breadcrumbs.scheduleSections.settings",
  settings: "breadcrumbs.scheduleSections.settings",
  importar: "breadcrumbs.scheduleSections.import",
  import: "breadcrumbs.scheduleSections.import",
  reservas: "breadcrumbs.scheduleSections.reservations",
  reservations: "breadcrumbs.scheduleSections.reservations",
  avaliacoes: "breadcrumbs.scheduleSections.ratings",
  ratings: "breadcrumbs.scheduleSections.ratings",
  historico: "breadcrumbs.scheduleSections.history",
  history: "breadcrumbs.scheduleSections.history",
};

const isScheduleTab = (tabSlug: string) => tabSlug === "programacao" || tabSlug === "schedule";

const PRODUCER_VARIANT: PanelVariant = {
  prefix: "/produtor",
  rootLabelKey: "breadcrumbs.root.producer",
  rootPath: "/produtor/dashboard",
  eventsSeg: "eventos",
  panelSlug: "painel",
  editVerb: "editar",
  sectionLabelKeys: {
    dashboard: DASHBOARD_KEY,
    eventos: "breadcrumbs.sections.events",
    events: "breadcrumbs.sections.events",
    financeiro: "breadcrumbs.sections.financial",
    financial: "breadcrumbs.sections.financial",
    inbox: "breadcrumbs.sections.inbox",
    "interest-lists": "breadcrumbs.sections.interestLists",
    "lista-de-interesse": "breadcrumbs.sections.interestLists",
    configuracoes: "breadcrumbs.sections.settings",
    settings: "breadcrumbs.sections.settings",
    organizacoes: "breadcrumbs.sections.organizations",
  },
  panelTabLabelKeys: {
    "": DASHBOARD_KEY,
    ingressos: "breadcrumbs.panelTabs.tickets",
    tickets: "breadcrumbs.panelTabs.tickets",
    pedidos: "breadcrumbs.panelTabs.orders",
    orders: "breadcrumbs.panelTabs.orders",
    participantes: "breadcrumbs.panelTabs.participants",
    participants: "breadcrumbs.panelTabs.participants",
    "lista-convidados": "breadcrumbs.panelTabs.guestlist",
    guestlist: "breadcrumbs.panelTabs.guestlist",
    checkin: "breadcrumbs.panelTabs.checkin",
    financeiro: "breadcrumbs.panelTabs.financial",
    financial: "breadcrumbs.panelTabs.financial",
    mensagens: "breadcrumbs.panelTabs.messages",
    messages: "breadcrumbs.panelTabs.messages",
    cupons: "breadcrumbs.panelTabs.coupons",
    coupons: "breadcrumbs.panelTabs.coupons",
    promotores: "breadcrumbs.panelTabs.promoters",
    promoters: "breadcrumbs.panelTabs.promoters",
    equipe: "breadcrumbs.panelTabs.team",
    team: "breadcrumbs.panelTabs.team",
    certificados: "breadcrumbs.panelTabs.certificates",
    certificates: "breadcrumbs.panelTabs.certificates",
    transmissao: "breadcrumbs.panelTabs.streaming",
    streaming: "breadcrumbs.panelTabs.streaming",
    revenda: "breadcrumbs.panelTabs.resales",
    resales: "breadcrumbs.panelTabs.resales",
    avaliacoes: "breadcrumbs.panelTabs.reviews",
    reviews: "breadcrumbs.panelTabs.reviews",
    programacao: "breadcrumbs.panelTabs.schedule",
    schedule: "breadcrumbs.panelTabs.schedule",
  },
};

const ADMIN_VARIANT: PanelVariant = {
  prefix: "/admin",
  rootLabelKey: "breadcrumbs.root.admin",
  rootPath: "/admin/relatorios",
  eventsSeg: "eventos",
  panelSlug: "painel",
  editVerb: "editar",
  sectionLabelKeys: {
    relatorios: "breadcrumbs.sections.reports",
    eventos: "breadcrumbs.sections.events",
    usuarios: "breadcrumbs.sections.users",
    produtores: "breadcrumbs.sections.producers",
    pedidos: "breadcrumbs.sections.orders",
    "relatorios-detalhados": "breadcrumbs.sections.complaints",
    financeiro: "breadcrumbs.sections.financial",
    saques: "breadcrumbs.sections.withdrawals",
    organizacoes: "breadcrumbs.sections.organizations",
    emails: "breadcrumbs.sections.emails",
    marketing: "breadcrumbs.sections.marketing",
    logs: "breadcrumbs.sections.logs",
    configuracoes: "breadcrumbs.sections.settings",
  },
  panelTabLabelKeys: {
    "": DASHBOARD_KEY,
    ingressos: "breadcrumbs.panelTabs.tickets",
    pedidos: "breadcrumbs.panelTabs.orders",
    participantes: "breadcrumbs.panelTabs.participants",
    "lista-convidados": "breadcrumbs.panelTabs.guestlist",
    checkin: "breadcrumbs.panelTabs.checkin",
    financeiro: "breadcrumbs.panelTabs.financial",
    mensagens: "breadcrumbs.panelTabs.messages",
    cupons: "breadcrumbs.panelTabs.coupons",
    promotores: "breadcrumbs.panelTabs.promoters",
    "equipe-evento": "breadcrumbs.panelTabs.eventTeam",
    equipe: "breadcrumbs.panelTabs.team",
    certificados: "breadcrumbs.panelTabs.certificates",
    transmissao: "breadcrumbs.panelTabs.streaming",
    revenda: "breadcrumbs.panelTabs.resales",
    avaliacoes: "breadcrumbs.panelTabs.reviews",
    programacao: "breadcrumbs.panelTabs.schedule",
    schedule: "breadcrumbs.panelTabs.schedule",
  },
};

/**
 * Trilha de navegação (breadcrumb) dos painéis do produtor e do admin.
 * Detecta o painel pelo pathname, monta os níveis clicáveis e resolve o
 * título do evento quando estamos dentro de um evento específico.
 */
export function PanelBreadcrumbs({ className }: { className?: string } = {}) {
  const { t } = useTranslation("producerCommon");
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const variant = useMemo(() => {
    if (location.pathname.startsWith("/admin")) return ADMIN_VARIANT;
    if (location.pathname.startsWith("/producer")) {
      return {
        ...PRODUCER_VARIANT,
        prefix: "/producer",
        rootPath: "/produtor/dashboard",
        eventsSeg: location.pathname.includes("/eventos") ? "eventos" : "events",
        panelSlug: location.pathname.includes("/painel") ? "painel" : "panel",
      };
    }
    return PRODUCER_VARIANT;
  }, [location.pathname]);

  const segments = useMemo(
    () =>
      location.pathname
        .replace(/^\/(producer|produtor|admin)\/?/, "")
        .split("/")
        .filter(Boolean),
    [location.pathname],
  );

  // Dentro de um evento específico? (busca o título)
  const eventId =
    segments[0] === variant.eventsSeg &&
    segments[1] &&
    segments[1] !== "novo" &&
    segments[1] !== "new"
      ? segments[1]
      : null;

  const { data: eventTitle } = useQuery({
    queryKey: ["panel-breadcrumb-event", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("title")
        .eq("id", eventId)
        .single();
      if (error) throw error;
      return data?.title as string | undefined;
    },
    enabled: !!eventId,
    staleTime: 60_000,
  });

  const editorStep = searchParams.get("editorStep");

  const crumbs = useMemo<Crumb[]>(() => {
    const list: Crumb[] = [{ label: t(variant.rootLabelKey), to: variant.rootPath }];
    const [s0, s1, s2, ...rest] = segments;

    // Raiz do painel
    const rootSeg = variant.prefix === "/admin" ? "relatorios" : "dashboard";
    if (!s0 || s0 === rootSeg) {
      const rootSegKey = variant.sectionLabelKeys[rootSeg];
      list.push({ label: rootSegKey ? t(rootSegKey) : t(DASHBOARD_KEY) });
      return list;
    }

    const sectionKey = variant.sectionLabelKeys[s0];
    const sectionLabel = sectionKey ? t(sectionKey) : s0;
    const sectionPath = `${variant.prefix}/${s0}`;

    // ----- Eventos -----
    if (s0 === variant.eventsSeg) {
      if (!s1) {
        list.push({ label: sectionLabel });
        return list;
      }
      list.push({ label: sectionLabel, to: sectionPath });

      if (s1 === "novo" || s1 === "new") {
        list.push({ label: t("breadcrumbs.newEvent") });
        return list;
      }

      const eventTitleLabel = eventTitle ?? t("breadcrumbs.eventFallback");
      const panelIndexPath = `${variant.prefix}/${variant.eventsSeg}/${s1}/${variant.panelSlug}`;

      if (s2 === variant.editVerb) {
        list.push({ label: eventTitleLabel, to: panelIndexPath });
        list.push({ label: t("breadcrumbs.edit") });
        return list;
      }

      if (s2 === "reports") {
        list.push({ label: eventTitleLabel, to: panelIndexPath });
        list.push({ label: t("breadcrumbs.eventReports") });
        return list;
      }

      if (s2 === "acessos") {
        list.push({ label: eventTitleLabel, to: panelIndexPath });
        list.push({ label: t("breadcrumbs.eventAccess") });
        return list;
      }

      if (s2 === variant.panelSlug) {
        // Edição inline dentro do painel (?editorStep=)
        const editStepKey = editorStep ? EDIT_STEP_KEYS[editorStep] : undefined;
        if (editStepKey) {
          list.push({ label: eventTitleLabel, to: panelIndexPath });
          list.push({ label: t(editStepKey) });
          return list;
        }
        const tabSlug = rest[0] ?? "";
        const tabKey = variant.panelTabLabelKeys[tabSlug];
        const tabLabel = tabKey ? t(tabKey) : tabSlug;
        if (!tabSlug) {
          list.push({ label: eventTitleLabel });
        } else if (isScheduleTab(tabSlug)) {
          // Programação tem subníveis: /programacao[/subnivel][/id]
          const tabPath = `${panelIndexPath}/${tabSlug}`;
          const subSlug = rest[1];
          const subKey = subSlug ? SCHEDULE_SUB_KEYS[subSlug] : undefined;
          list.push({ label: eventTitleLabel, to: panelIndexPath });
          if (!subSlug) {
            list.push({ label: tabLabel });
          } else if (rest[2]) {
            list.push({ label: tabLabel, to: tabPath });
            list.push({ label: subKey ? t(subKey) : subSlug, to: `${tabPath}/${subSlug}` });
            list.push({ label: t("breadcrumbs.detail") });
          } else {
            list.push({ label: tabLabel, to: tabPath });
            list.push({ label: subKey ? t(subKey) : subSlug });
          }
        } else {
          list.push({ label: eventTitleLabel, to: panelIndexPath });
          list.push({ label: tabLabel });
        }
        return list;
      }

      list.push({ label: eventTitleLabel });
      return list;
    }

    // ----- Listas de Interesse (produtor) -----
    if (s0 === "interest-lists" || s0 === "lista-de-interesse") {
      if (!s1) {
        list.push({ label: sectionLabel });
        return list;
      }
      list.push({ label: sectionLabel, to: sectionPath });
      list.push({
        label:
          s1 === "new" || s1 === "nova"
            ? t("breadcrumbs.interestLists.new")
            : t("breadcrumbs.interestLists.edit"),
      });
      return list;
    }

    // ----- Organizações -----
    if (s0 === "organizacoes") {
      if (variant.prefix === "/admin") {
        if (s1 === "cargos") {
          list.push({ label: t("breadcrumbs.sections.organizations"), to: sectionPath });
          list.push({ label: t("breadcrumbs.organizations.roles") });
        } else {
          list.push({ label: t("breadcrumbs.sections.organizations") });
        }
        return list;
      }
      // produtor
      if (s1 === "nova") {
        list.push({ label: t("breadcrumbs.organizations.new") });
        return list;
      }
      list.push({ label: t("breadcrumbs.organizations.single") });
      if (s2 === "configuracoes") list.push({ label: t("breadcrumbs.sections.settings") });
      else if (s2 === "membros") list.push({ label: t("breadcrumbs.organizations.members") });
      return list;
    }

    // ----- Produtores (admin) — detalhe -----
    if (s0 === "produtores" && s1) {
      list.push({ label: t("breadcrumbs.sections.producers"), to: sectionPath });
      list.push({ label: t("breadcrumbs.producers.detail") });
      return list;
    }

    // ----- Marketing (admin) — campanha -----
    if (s0 === "marketing" && s1) {
      list.push({ label: t("breadcrumbs.sections.marketing"), to: sectionPath });
      list.push({ label: t("breadcrumbs.marketing.campaign") });
      return list;
    }

    // ----- Seções simples -----
    list.push({ label: sectionLabel });
    return list;
  }, [segments, eventTitle, editorStep, variant, t]);

  return (
    <Breadcrumb className={cn("min-w-0", className)}>
      <BreadcrumbList className="flex-nowrap">
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <BreadcrumbItem key={`${crumb.label}-${i}`} className="min-w-0">
              {isLast || !crumb.to ? (
                <BreadcrumbPage className="truncate max-w-[10rem] sm:max-w-[16rem]">
                  {crumb.label}
                </BreadcrumbPage>
              ) : (
                <>
                  <BreadcrumbLink asChild>
                    <Link
                      to={crumb.to}
                      className="truncate max-w-[8rem] sm:max-w-[12rem] whitespace-nowrap"
                    >
                      {crumb.label}
                    </Link>
                  </BreadcrumbLink>
                  <BreadcrumbSeparator />
                </>
              )}
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
